import './i18n/i18n';
import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';

/*
 * Self-hosted fontovi (umesto Google Fonts CDN-a). GDPR razlog: ucitavanje
 * sa fonts.googleapis.com / fonts.gstatic.com salje IP adresu posetioca
 * Google-u u SAD bez consent-a (presuda LG Munchen 2022, 3 O 17493/20).
 * @fontsource paketi bundleovaju .woff2 fajlove u nasu statiku — webpack ih
 * servira sa balancepath.space domena. CSS deklaracije `font-family: 'Inter'`
 * i `font-family: 'Playfair Display'` rade isto kao i ranije.
 *
 * Tezine: Inter 300/400/400-italic/500/600/700; Playfair Display 400/700.
 * Italic 400 je potreban za <em>/<i> tagove u blog tekstovima, IPS uputstvima,
 * Terms-of-Use citatima i Monri card form pomocnom tekstu.
 */
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/400-italic.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/playfair-display/400.css';
import '@fontsource/playfair-display/700.css';

import './index.css';
import reportWebVitals from './reportWebVitals';
import App from './App';

/*
 * Auto-recovery od stale-cache buga posle deploy-a.
 *
 * Kad se posle deploy-a promene hash-evi na webpack chunk-ovima, otvoreni
 * tab-ovi sa starim index.html-om i dalje traze stare chunk fajlove
 * (`192.7042b4af.chunk.css` itd.) koji vise ne postoje. Browser dobija
 * 404, React lazy() puca sa "ChunkLoadError" i ekran ostane beo.
 *
 * Resenje: prvi put kad uhvatimo ChunkLoadError u sesiji, primoramo
 * window.location.reload(true) — browser tada povuce SVEZ index.html
 * (nginx ga ne kesira; vidi nginx.conf.example) i nove chunk-ove.
 *
 * sessionStorage flag sprecava beskonacnu petlju ako reload sam po sebi
 * ne pomogne (npr. CDN je propao) — pucamo samo jedanput, posle toga
 * pustamo error da padne kako bi se video pravi root cause.
 */
const CHUNK_RELOAD_FLAG = 'bp-chunk-reload-attempted';

const isChunkLoadError = (err) => {
    if (!err) return false;
    const name = err.name || (err.error && err.error.name);
    const msg = err.message || (err.error && err.error.message) || '';
    return (
        name === 'ChunkLoadError' ||
        /Loading (CSS )?chunk \d+ failed/i.test(msg) ||
        /Failed to fetch dynamically imported module/i.test(msg)
    );
};

const tryReloadOnChunkError = (err) => {
    if (!isChunkLoadError(err)) return;
    if (sessionStorage.getItem(CHUNK_RELOAD_FLAG)) return;
    sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
    window.location.reload();
};

window.addEventListener('error', (event) => tryReloadOnChunkError(event));
window.addEventListener('unhandledrejection', (event) =>
    tryReloadOnChunkError(event.reason),
);

const normalizePath = (path) => {
    const trimmed = (path || '/').replace(/\/+$/, '');
    return trimmed === '' ? '/' : trimmed;
};

const container = document.getElementById('root');
const rootElement = <App />;

// Postbuild skripta (scripts/inject-prerender-marker.js) ubacuje u svaki pre-renderovan
// HTML meta tag oblika <meta name="x-prerendered-path" content="/trps" />. Hydrate je
// siguran SAMO kad sadržaj tog tag-a odgovara trenutnoj ruti. Ako se ne poklapa (nginx
// je za ne-pre-renderovanu rutu fallback-ovao na landing /index.html), brišemo
// pre-renderovan DOM i renderujemo čisto kroz createRoot — sprečava hydration mismatch
// koji se manifestuje kao beli ekran pri prvom učitavanju.
const prerenderMeta = document.querySelector('meta[name="x-prerendered-path"]');
const prerenderedPath = prerenderMeta && prerenderMeta.getAttribute('content');
const currentPath = normalizePath(window.location.pathname);

const canHydrate =
    prerenderedPath != null &&
    normalizePath(prerenderedPath) === currentPath &&
    container.hasChildNodes();

if (canHydrate) {
    hydrateRoot(container, rootElement);
} else {
    if (container.hasChildNodes()) {
        container.innerHTML = '';
    }
    createRoot(container).render(rootElement);
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
